import { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { auth, googleProvider } from "../../config/firebase";
import {
  signInWithPopup,
  GoogleAuthProvider,
  signInWithCredential,
} from "firebase/auth";
import "./login-gate.css";

const GOOGLE_CLIENT_ID =
  "922243309730-la2f7s25jb274ko3q8q9rc5arttsiu5b.apps.googleusercontent.com";

const LoginGate = ({ onComplete }) => {
  // Create the portal container once, synchronously — so portal renders immediately
  const [container] = useState(() => {
    const el = document.createElement("div");
    el.id = "auth-gate-root";
    return el;
  });

  const observerRef = useRef(null);
  const dismissedRef = useRef(false);
  const [signingIn, setSigningIn] = useState(false);

  // Unified dismiss — called on both sign-in and skip
  const dismiss = useCallback(
    (user) => {
      if (dismissedRef.current) return;
      dismissedRef.current = true;

      sessionStorage.setItem("auth_decision", user ? "signed_in" : "skipped");
      document.body.classList.remove("auth-gate-active");

      // Remove inline style lock from #root
      const rootEl = document.getElementById("root");
      if (rootEl) {
        rootEl.style.removeProperty("filter");
        rootEl.style.removeProperty("pointer-events");
        rootEl.style.removeProperty("user-select");
      }

      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      onComplete(user ?? null);
    },
    [onComplete],
  );

  // Google One Tap JWT callback → Firebase credential
  const handleOneTapCallback = useCallback(
    async (response) => {
      try {
        const credential = GoogleAuthProvider.credential(response.credential);
        const result = await signInWithCredential(auth, credential);
        dismiss(result.user);
      } catch (err) {
        console.error("One Tap sign-in error:", err);
      }
    },
    [dismiss],
  );

  // Regular popup sign-in (fallback button)
  const handleSignIn = async () => {
    setSigningIn(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      dismiss(result.user);
    } catch (err) {
      // Ignore popup closed by user
      if (err.code !== "auth/popup-closed-by-user") {
        console.error("Sign in error:", err);
      }
    } finally {
      setSigningIn(false);
    }
  };

  // Auto-trigger Google sign-in popup on mount (slight delay so card animates in first)
  useEffect(() => {
    const timer = setTimeout(() => handleSignIn(), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, []);

  // Append portal container to body, add blur class, start MutationObserver
  useEffect(() => {
    document.body.appendChild(container);
    document.body.classList.add("auth-gate-active");

    const rootEl = document.getElementById("root");

    // Re-applies every protection. Only sets a property when it has been
    // tampered with so we never cause unnecessary attribute mutations.
    const applyProtection = () => {
      if (!document.body.classList.contains("auth-gate-active")) {
        document.body.classList.add("auth-gate-active");
      }
      if (!document.body.contains(container)) {
        document.body.appendChild(container);
      }
      if (rootEl) {
        if (
          rootEl.style.getPropertyValue("filter") !== "blur(8px)" ||
          rootEl.style.getPropertyPriority("filter") !== "important"
        ) {
          rootEl.style.setProperty("filter", "blur(8px)", "important");
        }
        if (
          rootEl.style.getPropertyValue("pointer-events") !== "none" ||
          rootEl.style.getPropertyPriority("pointer-events") !== "important"
        ) {
          rootEl.style.setProperty("pointer-events", "none", "important");
        }
        if (
          rootEl.style.getPropertyValue("user-select") !== "none" ||
          rootEl.style.getPropertyPriority("user-select") !== "important"
        ) {
          rootEl.style.setProperty("user-select", "none", "important");
        }
      }
    };

    // Apply inline !important lock immediately
    applyProtection();

    // Watch for:
    //  • container being removed (childList on body)
    //  • auth-gate-active class being removed (attributes on body)
    //  • inline styles being removed (attributes on #root)
    const observer = new MutationObserver(applyProtection);
    observer.observe(document.body, {
      childList: true,
      attributes: true,
      attributeFilter: ["class"],
    });
    if (rootEl) {
      observer.observe(rootEl, {
        attributes: true,
        attributeFilter: ["style"],
      });
    }
    observerRef.current = observer;

    return () => {
      observer.disconnect();
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
      document.body.classList.remove("auth-gate-active");
      if (rootEl) {
        rootEl.style.removeProperty("filter");
        rootEl.style.removeProperty("pointer-events");
        rootEl.style.removeProperty("user-select");
      }
    };
  }, [container]);

  // Initialize Google One Tap after GIS script loads
  useEffect(() => {
    const init = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleOneTapCallback,
          auto_select: true,
          cancel_on_tap_outside: false,
          context: "signin",
        });
        window.google.accounts.id.prompt();
      }
    };

    if (window.google?.accounts?.id) {
      init();
    } else {
      // GIS script loads async — wait for it
      const timer = setTimeout(init, 2000);
      return () => clearTimeout(timer);
    }
  }, [handleOneTapCallback]);

  return ReactDOM.createPortal(
    <div className="auth-gate__backdrop">
      <div className="auth-gate__card">
        <div className="auth-gate__icon">
          <i className="bx bx-user-circle"></i>
        </div>

        <h2 className="auth-gate__title">Welcome</h2>

        <button
          className="auth-gate__google-btn"
          onClick={handleSignIn}
          disabled={signingIn}
          type="button"
        >
          {signingIn ? (
            <span className="auth-gate__btn-loading">Signing in…</span>
          ) : (
            <>
              <svg
                className="auth-gate__google-icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Sign in with Google
            </>
          )}
        </button>

        <button
          className="auth-gate__skip-btn"
          onClick={() => dismiss(null)}
          type="button"
        >
          Continue without signing in
        </button>
      </div>
    </div>,
    container,
  );
};

export default LoginGate;
